import React, { useEffect, useRef, useState } from "react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import type { ExposureReactionDossierCaptureV3 } from "@shared/exposureReactionDossier";
import ExposureReactionDossierDisplay, { type ExposureReactionDossierVersionSummary } from "@/components/ExposureReactionDossierDisplay";

type SavedResponse = {
  dossier: { id: string; currentReportId: string; questionId: string };
  report: { id: string; title?: string };
  capture: ExposureReactionDossierCaptureV3;
  manifestDigest: string;
  versions: ExposureReactionDossierVersionSummary[];
};
type ReassessRequest = { dossierId: string; selectedReportId: string; expectedParentReportId: string; requestId: string };

function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  throw new Error("This browser cannot create a secure request ID. Update the browser and try again.");
}

async function bodyOf(response: Response): Promise<Record<string, unknown>> {
  try {
    const value: unknown = await response.json();
    return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  } catch { return {}; }
}

const ExposureReactionDossierReport: React.FC<{ reportId: string; onUpdated: (reportId: string) => void }> = ({ reportId, onUpdated }) => {
  const reportIdRef = useRef(reportId);
  reportIdRef.current = reportId;
  const [data, setData] = useState<SavedResponse | null>(null);
  const [notDossier, setNotDossier] = useState(false);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<ReassessRequest | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setNotDossier(false);
    setError(null);
    setBusy(false);
    setPending(null);
    fetch(`/api/research-dossiers/exposure/reports/${encodeURIComponent(reportId)}`, {
      headers: getClientIdentityHeaders(), credentials: "include", cache: "no-store", signal: controller.signal,
    }).then(async (response) => {
      const payload = await bodyOf(response);
      if (controller.signal.aborted) return;
      if (response.status === 404) { setNotDossier(true); return; }
      if (!response.ok) {
        const detail = typeof payload.detail === "string" ? `: ${payload.detail}` : ".";
        setError({ message: `Saved exposure dossier could not be read (HTTP ${response.status})${detail}`, retryable: false });
        return;
      }
      if (!payload.capture || !payload.dossier || !Array.isArray(payload.versions)) {
        setError({ message: "The saved exposure dossier response is incomplete.", retryable: false });
        return;
      }
      setData(payload as unknown as SavedResponse);
    }).catch((cause: unknown) => {
      if (!controller.signal.aborted) setError({ message: cause instanceof Error ? `Saved exposure dossier could not be read: ${cause.message}` : "Saved exposure dossier could not be read.", retryable: false });
    });
    return () => controller.abort();
  }, [reportId]);

  const sendReassessment = async (request: ReassessRequest) => {
    if (!data || request.selectedReportId !== reportIdRef.current) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/api/research-dossiers/exposure/${encodeURIComponent(request.dossierId)}/reassess`, {
        method: "POST", credentials: "include",
        headers: { ...getClientIdentityHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ expectedParentReportId: request.expectedParentReportId, requestId: request.requestId }),
      });
      const payload = await bodyOf(response);
      if (request.selectedReportId !== reportIdRef.current) return;
      if (!response.ok) {
        const detail = typeof payload.detail === "string" ? `: ${payload.detail}` : ".";
        setError({ message: `Manual reassessment failed with HTTP ${response.status}${detail}`, retryable: response.status >= 500 });
        return;
      }
      const report = payload.report;
      if (!report || typeof report !== "object" || Array.isArray(report) || typeof (report as Record<string, unknown>).id !== "string") {
        setError({ message: "The server response is incomplete. The reassessment may have been saved; retry with the same request ID.", retryable: true });
        return;
      }
      setPending(null);
      onUpdated((report as { id: string }).id);
    } catch {
      if (request.selectedReportId === reportIdRef.current) setError({ message: "No HTTP response was received. The reassessment result is unknown.", retryable: true });
    } finally {
      if (request.selectedReportId === reportIdRef.current) setBusy(false);
    }
  };

  const handleReassess = () => {
    if (!data || busy || reportId !== data.dossier.currentReportId) return;
    try {
      const request = { dossierId: data.dossier.id, selectedReportId: reportId, expectedParentReportId: reportId, requestId: uuid() };
      setPending(request);
      void sendReassessment(request);
    } catch (cause) { setError({ message: cause instanceof Error ? cause.message : "Could not create a secure request ID.", retryable: false }); }
  };

  if (notDossier) return null;
  if (!data && !error) return <section className="reports-detail-evidence" aria-live="polite">Loading saved exposure dossier…</section>;

  return <section aria-label="Saved exposure and reaction dossier">
    {error && <div className="analytics-error" role="alert" aria-live="assertive">
      <p>{error.message}</p>
      {error.retryable && pending && <button type="button" className="analytics-action-btn" onClick={() => void sendReassessment(pending)} disabled={busy}>Retry the same reassessment</button>}
    </div>}
    {data && <>
      <div className="reports-detail-evidence">
        <h3>Manual reassessment</h3>
        <p>A new assessment runs only when you select this button. It uses the saved definition and compares with the current saved version.</p>
        {reportId !== data.dossier.currentReportId && <p>This is an older saved version. Open the latest version before reassessing: <a href={`/reports?id=${encodeURIComponent(data.dossier.currentReportId)}`}>Open latest version</a>.</p>}
        <button type="button" className="analytics-action-btn" onClick={handleReassess} disabled={busy || reportId !== data.dossier.currentReportId || (!!pending && error?.retryable === true)}>
          {busy ? "Saving reassessment…" : "Reassess now"}
        </button>
      </div>
      <ExposureReactionDossierDisplay capture={data.capture} versions={data.versions} />
    </>}
  </section>;
};

export default ExposureReactionDossierReport;
